import groupBy from 'lodash/groupBy'
import { EstimatedItem } from '../../domain/workareaItem'
import { groupKey, getGroupName } from '../Items/grouping'
import { CatalogItem } from '../../domain/catalogItem'
import { sum } from '../../helpers/array'
import { ItemClass, PricingTypes } from '../../common/constants'
import { ServerState } from './proposalsModel'
import { ProposalAttachment, appendAttachment } from './proposalAttachmentsModel'

export interface LineItem {
  id: number,
  title: string,
  enabled: boolean,
  locked: boolean,
  price: number,
  estimatedPrice: number,
  pricingType: number,
  description: string,
  downPaymentInvoiceDescription: string | null,
  costCode: CostCode,
  costCodes: CostCode[],
  isOnHold: boolean,
  sortOrder: number
  workareaIds: number[]
  showItems: boolean
  selectedItemGuids: string[]
  selectedItemColumns: string[]
  attachments?: ProposalAttachment[]
  attachmentIds?: string[]
}

export const defaultLineItem = (): LineItem => ({
  id: 0,
  title: '',
  enabled: true,
  locked: false,
  price: 0,
  estimatedPrice: 0,
  pricingType: 0,
  description: '',
  downPaymentInvoiceDescription: null,
  costCode: { id: 0, qboId: 0, name: '' },
  costCodes: [],
  isOnHold: false,
  sortOrder: 0,
  workareaIds: [],
  showItems: false,
  selectedItemGuids: [],
  selectedItemColumns: [],
  attachments: []
})

export interface CostCode {
  id: number,
  name: string,
  qboId: number | null | undefined
}

export type ItemGrouping = {
  key: string
  name: string
  quantity: number
  units: string
  class: ItemClass
  totalPrice: number
  selected: boolean
  itemGuids: string[]
}

export enum ActionType {
  EnabledChanged = 'enabledChanged',
  LockedChanged = 'lockedChanged',
  DescriptionChanged = 'descriptionChanged',
  DownPaymentInvoiceDescriptionChanged = 'downPaymentInvoiceDescriptionChanged',
  ShowItemsChanged = 'showItemsChanged',
  SelectedItemsChanged = 'selectedItemsChanged',
  SelectedItemColumnsChanged = 'selectedItemColumnsChanged',
  ItemColumnToggled = 'itemColumnToggled',
  ItemClassesChanged = 'itemClassesChanged',
  AttachmentsChanged = 'attachmentsChanged',
  AttachmentAdded = 'attachmentAdded',
}

export type Action =
  | { type: ActionType.EnabledChanged, value: boolean }
  | { type: ActionType.LockedChanged, value: boolean }
  | { type: ActionType.DescriptionChanged, value: string }
  | { type: ActionType.DownPaymentInvoiceDescriptionChanged, value: string }
  | { type: ActionType.ShowItemsChanged, value: boolean, serverState: ServerState }
  | { type: ActionType.SelectedItemsChanged, value: string[] }
  | { type: ActionType.SelectedItemColumnsChanged, value: string[] }
  | { type: ActionType.ItemColumnToggled, value: string, enabled: boolean }
  | { type: ActionType.ItemClassesChanged, classId: ItemClass, checked: boolean, serverState: ServerState }
  | { type: ActionType.AttachmentsChanged, value: ProposalAttachment[] }
  | { type: ActionType.AttachmentAdded, value: ProposalAttachment }

export const setEnabled = (value: boolean): Action => {
  return { type: ActionType.EnabledChanged, value }
}

export const setLocked = (value: boolean): Action => {
  return { type: ActionType.LockedChanged, value }
}

export const setDescription = (value: string): Action => {
  return { type: ActionType.DescriptionChanged, value }
}

export const setDownPaymentInvoiceDescription = (value: string): Action => {
  return { type: ActionType.DownPaymentInvoiceDescriptionChanged, value }
}

export const setSelectedItemGuids = (value: string[]): Action => {
  return { type: ActionType.SelectedItemsChanged, value }
}

export const setSelectedItemColumns = (value: string[]): Action => {
  return { type: ActionType.SelectedItemColumnsChanged, value }
}

export const setShowItems = (value: boolean, serverState: ServerState): Action => {
  return { type: ActionType.ShowItemsChanged, value, serverState }
}

export const setAttachments = (value: ProposalAttachment[]): Action => {
  return { type: ActionType.AttachmentsChanged, value }
}

export const addAttachment = (value: ProposalAttachment): Action => {
  return { type: ActionType.AttachmentAdded, value }
}

export const reducer = (state: LineItem, action: Action): LineItem => {
  switch (action.type) {
    case ActionType.EnabledChanged:
      return { ...state, enabled: action.value }
    case ActionType.LockedChanged:
      return { ...state, locked: action.value }
    case ActionType.DescriptionChanged:
      return { ...state, description: action.value }
    case ActionType.DownPaymentInvoiceDescriptionChanged:
      return { ...state, downPaymentInvoiceDescription: action.value }
    case ActionType.ShowItemsChanged:
      return showItems(state, action.value, action.serverState)
    case ActionType.SelectedItemsChanged:
      return { ...state, selectedItemGuids: action.value }
    case ActionType.SelectedItemColumnsChanged:
      return { ...state, selectedItemColumns: action.value }
    case ActionType.ItemColumnToggled:
      return toggleItemColumn(state, action.value, action.enabled)
    case ActionType.ItemClassesChanged:
      return toggleItemClass(state, action.serverState, action.classId, action.checked)
    case ActionType.AttachmentsChanged:
      return { ...state, attachments: action.value }
    case ActionType.AttachmentAdded:
      return { ...state, attachments: appendAttachment(state.attachments ?? [], action.value) }
  }
}

export const renderPricingType = (number: number) => {
  switch (number) {
    case 0: return 'Time and Materials'
    case 1: return 'Quoted Price'
    case 2: return 'Flat Rate'
    case 3: return 'Unbillable'
    case 4: return 'Per Visit'
    default: return `Other (${number})`
  }
}

export const getGroupedItems = (items: EstimatedItem[], catalogItems: CatalogItem[]): ItemGrouping[] => {
  const groups = groupBy(items, groupKey) as Record<string, EstimatedItem[]>
  return Object.entries(groups).map(([key, value]) => ({
    key,
    name: getGroupName({ ...value[0], isFromTimesheets: false }, catalogItems),
    quantity: sum(value, x => x.quantity),
    units: value[0].units,
    totalPrice: sum(value, x => x.totalPrice),
    class: value[0].class,
    itemGuids: value.map(x => x.guid),
    selected: false
  }))
}

export const showItems = (lineItem: LineItem, value: boolean, serverState: ServerState): LineItem => {
  const estimatedItemsForLineItem = getEstimatedItemsForLineItem(lineItem, serverState)
  const newSelectedItemGuids = value && lineItem.selectedItemGuids.length === 0 
    ? estimatedItemsForLineItem.map(x => x.guid)
    : lineItem.selectedItemGuids
  return {
    ...lineItem,
    showItems: value,
    selectedItemGuids: newSelectedItemGuids
  }
}

export const toggleItemColumn = (lineItem: LineItem, value: string, enabled: boolean): LineItem => {
  if (enabled) {
    if (!lineItem.selectedItemColumns.includes(value)) {
      return {
        ...lineItem,
        selectedItemColumns: [...lineItem.selectedItemColumns, value]
      }
    }

    return lineItem
  } else {
    return {
      ...lineItem,
      selectedItemColumns: lineItem.selectedItemColumns.filter(x => x !== value)
    }
  }
}

const getEstimatedItemsForLineItem = (lineItem: LineItem, serverState: ServerState): EstimatedItem[] => {
  return serverState.estimatedItems.filter(x => lineItem.workareaIds.includes(x.workareaId))
}

export const toggleItemClass = (lineItem: LineItem, serverState: ServerState, c: ItemClass, checked: boolean): LineItem => {
  const estimatedItemsForLineItem = getEstimatedItemsForLineItem(lineItem, serverState)
  if (checked) {
    const newSelectedItemGuids = estimatedItemsForLineItem.filter(x => x.class === c).map(x => x.guid)
    const uniqueSelectedItemGuids = Array.from(new Set([...lineItem.selectedItemGuids, ...newSelectedItemGuids]))
    return {
      ...lineItem,
      selectedItemGuids: uniqueSelectedItemGuids
    }
  } else {
    return {
      ...lineItem,
      selectedItemGuids: lineItem.selectedItemGuids.filter(x => estimatedItemsForLineItem.find(y => y.guid === x)?.class !== c)
    }
  }
}
export const getDownPaymentInvoiceLineItems = (lineItems: LineItem[]): LineItem[] => {
  return lineItems.filter(x => x.enabled && x.pricingType === PricingTypes.QuotedPrice)
}
